import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, Stack, SvgIconProps, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { WasteManifestSource } from "generated-graphql/graphql";
import { ComponentType } from "react";
import { Link as RouterLink } from "react-router-dom";

import { SourceChipState } from "./billOfLadingSummary";
import { ManifestDataSources } from "./ManifestDataSources";
import { SectionHeader } from "./SectionHeader";
import { StatusLevel, SummaryItem } from "./summaryStatus";

/** Encircled bullet icon + color per status level. */
const LEVEL_ICON: Record<StatusLevel, { Icon: ComponentType<SvgIconProps>; color: string }> = {
  ok: { Icon: CheckCircle, color: "success.main" },
  warning: { Icon: ErrorIcon, color: "warning.main" },
  error: { Icon: ErrorIcon, color: "error.main" },
  info: { Icon: InfoIcon, color: "info.main" },
  neutral: { Icon: InfoIcon, color: "text.disabled" },
};

/** The link and trailing text that follow an item's message on the same line. */
function SummaryItemExtras({ item }: { item: SummaryItem }) {
  const { link, trailingText } = item;
  return (
    <>
      {link && (
        <Typography variant="body2">
          {link.to ? <RouterLink to={link.to}>{link.text}</RouterLink> : link.text}
        </Typography>
      )}
      {trailingText && (
        <Typography variant="body2" color="text.secondary">
          {trailingText}
        </Typography>
      )}
    </>
  );
}

/** The item's action buttons, on their own line under its message. */
function SummaryItemActions({ actions }: { actions: NonNullable<SummaryItem["actions"]> }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {actions.map((action) => (
        <Button
          key={action.label}
          size="small"
          color="inherit"
          onClick={action.onClick}
          sx={{ minWidth: 0, py: 0, px: 0.5 }}
        >
          {action.label}
        </Button>
      ))}
    </Stack>
  );
}

interface SummarySectionProps {
  items: SummaryItem[];
  /** When provided, renders the Data Sources chip panel as a right-hand column. */
  availableSources?: readonly WasteManifestSource[];
  /** Which sources get a chip, in order; every source when omitted. */
  sourceOrder?: readonly WasteManifestSource[];
  /** A document pillar exists but is pending review (see ManifestDataSources). */
  documentPendingReview?: boolean;
  /** Makes the Data Sources pending-review marker open the review screen. */
  onReviewDocument?: () => void;
  /** Per-source chip state that replaces the available/pending default (bills of lading). */
  chipStates?: Partial<Record<WasteManifestSource, SourceChipState>>;
  /** Shows the bill of lading Data Sources explainer. */
  isBillOfLading?: boolean;
}

/**
 * At-a-glance manifest health as a human-readable, icon-bulleted list, with the
 * Data Sources chip panel as a second column on the right (headers aligned at
 * the top). Every line restates something available in detail elsewhere in the
 * drawer; this is the quick read. Source-agnostic.
 */
export function SummarySection({
  items,
  availableSources,
  sourceOrder,
  documentPendingReview,
  onReviewDocument,
  chipStates,
  isBillOfLading,
}: SummarySectionProps) {
  if (items.length === 0 && !availableSources) return null;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SectionHeader title="Summary" />
        <Stack spacing={0.75}>
          {items.map((item) => {
            const { Icon, color } = LEVEL_ICON[item.level];
            return (
              <Stack key={item.key} direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ display: "flex", alignItems: "center", height: 20, flexShrink: 0 }}>
                  <Icon sx={{ fontSize: "1.125rem", color }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">{item.message}</Typography>
                    <SummaryItemExtras item={item} />
                    {item.tooltip && (
                      <InfoIconTooltip
                        tooltip={item.tooltip}
                        fontSize="1rem"
                        iconButtonSx={{ p: 0 }}
                      />
                    )}
                  </Stack>
                  {item.actions && <SummaryItemActions actions={item.actions} />}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Box>
      {availableSources && (
        <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "20%" } }}>
          <ManifestDataSources
            availableSources={availableSources}
            sourceOrder={sourceOrder}
            documentPendingReview={documentPendingReview}
            onReviewDocument={onReviewDocument}
            chipStates={chipStates}
            isBillOfLading={isBillOfLading}
          />
        </Box>
      )}
    </Stack>
  );
}
